import { COLOR } from 'admin/constants/styles';
import React from 'react';
import styled, { css } from 'styled-components';

type ButtonProps = {
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'] | 'outline';
  variant?: 'contained' | 'outline' | 'neutral' | 'muted-outline' | 'danger' | 'warning';
  disabled?: boolean;
};

type StyleMap = Record<Required<ButtonProps>['variant'], ReturnType<typeof css>>;

const containedStyles = css<ButtonProps>`
  ${({ disabled }) =>
    !disabled
      ? css`
          box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
          -webkit-box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
          -moz-box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
          background: ${COLOR.corporatePrimaryColor};
          color: white;
          &:hover {
            background: #009239;
          }
        `
      : css`
          background: #dfdfdf;
          opacity: 0.75;
        `}
`;

const outlineStyles = css<ButtonProps>`
  background: white;
  border: solid 1px ${COLOR.corporatePrimaryColor};
  ${({ disabled }) =>
    !disabled
      ? css`
          color: ${COLOR.corporatePrimaryColor};
          &:hover {
            background: #eee;
          }
        `
      : css`
          border-color: #dfdfdf;
          opacity: 0.75;
        `}
`;

const neutralStyles = css<ButtonProps>`
  background: #edeff1;
  border: solid 1px #dfdfdf;
  ${({ disabled }) =>
    !disabled
      ? css`
          color: #757575;
          &:hover {
            background: #f4f4f4;
          }
        `
      : css`
          opacity: 0.75;
        `}
`;

const mutedOutlineStyles = css<ButtonProps>`
  background: white;
  border: solid 1px #d1d5da;
  color: #757575;
  ${({ disabled }) =>
    !disabled
      ? css`
          &:hover {
            color: #a3a3a3;
          }
        `
      : css`
          opacity: 0.50;
        `}
`;

const dangerStyles = css<ButtonProps>`
  background: #d75f48;
  color: white;
  ${({ disabled }) =>
    !disabled
      ? css`
          box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
          -webkit-box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
          -moz-box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
          &:hover {
            background: #c04f3a;
          }
        `
      : css`
          opacity: 0.75;
        `}
`;

const warningStyles = css<ButtonProps>`
  background: #E6AE06;
  color: white;
  ${({ disabled }) =>
    !disabled
      ? css`
          box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
          -webkit-box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
          -moz-box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
          &:hover {
            background: #CC9A06;
          }
        `
      : css`
          opacity: 0.75;
        `}
`;

const styleMap: StyleMap = {
  contained: containedStyles,
  outline: outlineStyles,
  neutral: neutralStyles,
  'muted-outline': mutedOutlineStyles,
  danger: dangerStyles,
  warning: warningStyles,
};

export const Button = styled.button<ButtonProps>`
  padding: 5px 15px;
  border: none;
  border-radius: 3px;
  min-width: 100px;
  transition: all 0.2s ease-in-out;
  &:active {
    transform: scale(0.98);
  }

  ${({ disabled }) => (disabled ? 'cursor: default !important;' : '')};
  ${({ disabled }) => (disabled ? 'pointer-events: none;' : '')};

  ${({ variant = 'contained' }) => styleMap[variant] ?? styleMap.contained}
`;

export default Button;
